import React, { useEffect, useState } from 'react';
import { cqAPI } from '../../lib/Auth';
import {
  Text,
  Center,
  Button,
  VStack,
  Box,
  Flex,
  HStack,
  useTheme,
  useToast,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import ReactSelect from "react-select";

function AdecSettings() {
  const [isConnected, setIsConnected] = useState(false);
  const [link, setLink] = useState('');
  const [accountName, setAccountName] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [providers, setProviders] = useState([]);
  const [isCleaning, setIsCleaning] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [newProvider, setNewProvider] = useState({});
  const [selectValues, setSelectValues] = useState({});
  const [refreshCounter, setRefreshCounter] = useState(0);
  const theme = useTheme();
  const toast = useToast();
  const {
    isOpen,
    onOpen,
    onClose,
  } = useDisclosure();

  const customStyles = {
    control: (provided) => ({
      ...provided,
      width: "250px",
      backgroundColor: theme.colors.lightBackground,
      borderColor: theme.colors.gray[300],
      borderRadius: '8px',
      "&:hover": {
        borderColor: theme.colors.gray[400],
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.gray[50],
      width: "max-content",
      minWidth: "100%"
    }),
    option: (provided, state) => ({
      ...provided,
      textAlign: "left",
      backgroundColor: state.isSelected
        ? theme.colors.blue[100]
        : theme.colors.gray[50],
      color: state.isDisabled
        ? theme.colors.gray[400]
        : theme.colors.lightText,
      "&:hover": {
        backgroundColor: theme.colors.blue[50],
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      // width: "200px",
      justifyContent: "flex-start",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme.colors.brand[300],
      "&:hover": {
        color: theme.colors.brand[300],
      },
    }),
    // Add other style overrides as needed
  };

  const getProfile = async () => {
    try {
      const response = await cqAPI.get('/settings/a-dec');
      if (response.type === 'ok') {
        setIsConnected(response.data.connected);
        setLink(response.data.link || '');
        setProfiles(response.data.profiles || []);
        setProviders(response.data.providers || []);
        setAccountName(response.data.accountName || '');
      } else if (response.type === 'error') {
        return toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getProfile();
    setIsCleaning(false);
    setIsReassigning(false);
  }, []);

  useEffect(() => {
    const checkErrors = async () => {
      // Check for A-dec connection errors
      const response = await cqAPI.get('/settings/a-dec/error');
      if (response.type === 'ok') return;
      if (response.type === 'error') {
        return toast({
          title: `Connection Failed ${response.error_code ? '('+response.error_code+')' : ''}`,
          status: "error",
          description: response.msg,
          duration: 5000,
          isClosable: true,
        });
      }
    }
    checkErrors();
  }, []);

  const updateProvider = async (providerData = newProvider, clearHistory = false) => {
    const response = await cqAPI.post("/settings/a-dec/provider", {
      profile_id: profiles[providerData.index].adec_id,
      provider_id: providerData.selectedOption ? providerData.selectedOption.value : null,
      clear_history: clearHistory
    });

    if (response.type === "ok") {
      setProfiles((prevProfiles) => {
        const updatedProfiles = [...prevProfiles];
        updatedProfiles[providerData.index] = {
          ...updatedProfiles[providerData.index],
          provider_id: providerData.selectedOption ? providerData.selectedOption.value : null
        };
        return updatedProfiles;
      });
      // Remove the temporary controlled value for this select
      setSelectValues((prev) => {
        const newValues = { ...prev };
        delete newValues[providerData.index];
        return newValues;
      });
      setIsCleaning(false);
      setIsReassigning(false);
      if (response.msg) {
        return toast({
          title: "Association Complete",
          status: "success",
          description: response.msg,
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }
    toast({
      title: "Error matching the provider",
      status: "error",
      duration: 7000,
      isClosable: true,
    });
  }

  const handleProviderChange = async (profile, providerData) => {
    const response = await cqAPI.get("/settings/a-dec/provider/history", {
      profile_id: profile.adec_id,
    });
    if (response.type === 'ok' && response.data?.has_history) {
      onOpen();
    } else {
      await updateProvider(providerData);
    }
  }

  const handleModalClose = () => {
    setIsCleaning(false);
    if (newProvider.index !== undefined) {
      setSelectValues((prev) => {
        const newValues = { ...prev };
        delete newValues[newProvider.index];
        return newValues;
      });
    }
    setNewProvider({});
    setRefreshCounter((prev) => prev + 1);
    onClose();
  };

  const intercomCleanerModal = (
    <Modal isOpen={isOpen} onClose={handleModalClose}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader fontWeight='bold' textAlign="left">Confirm</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          What would you like to do with messages that were sent to the existing profile?
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' color='red' fontSize='sm' onClick={async () => {
            setIsCleaning(true);
            await updateProvider(newProvider, true);
            onClose();
          }}>
            Delete all
            {isCleaning && <Spinner size="sm" ml="2"/>}
          </Button>
          <Spacer/>
          <Button mr={3} onClick={async () => {
            setIsReassigning(true);
            await updateProvider();
            onClose();
          }}>
            Reassign to this user
            {isReassigning && <Spinner size="sm" ml="2"/>}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const disconnect = async () => {
    const response = await cqAPI.del("/settings/a-dec/disconnect");
    if (response.type === 'ok') {
      response.msg && toast({
        title: "A-dec+ Integration",
        description: response.msg,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      getProfile();
    } else if (response.type === 'error') {
      return toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    }
  }

  return (
    <>
      {isConnected ? (
        <Box p={4} overflowY="auto" height="100%" width="100%">
          {intercomCleanerModal}
          <HStack>
            <Flex justifyContent="flex-start" mb={4}>
              <Text className="page-header"> A-dec+ Profile Link </Text>
            </Flex>
            <Spacer/>
            <Flex justifyContent="flex-end" mb={4} mr={5} mt={5} direction={"column"} alignItems={"flex-end"} >
              <Button onClick={disconnect} maxW={120} mb={2}>
                Disconnect
              </Button>
              {accountName && <Text> Connected as {accountName}</Text>}
            </Flex>
          </HStack>
          <Flex borderBottom="1px" borderColor={theme.colors.dividerColor.light} p={2}>
            <HStack spacing={8} flex="1">
              <Text
                pl={2}
                textAlign="left"
                fontSize="sm"
                flexBasis="20%"
                fontWeight="bold"
                cursor="pointer"
              >
                A-dec+ Profiles
              </Text>
              <Text
                fontSize="sm"
                ml={20}
                fontWeight="bold"
                cursor="pointer"
              >
                Link AI Profiles
              </Text>
            </HStack>
          </Flex>
          {profiles.map((profile, index) => {
            // Exclude providers already assigned to other profiles.
            const usedProviderIds = profiles
              .filter((_, i) => i !== index)
              .map((p) => p.provider_id)
              .filter(Boolean);

            const options = providers.map((provider) => {
              const isCurrentProvider = profile.provider_id === provider.id;
              const isUsedByOtherProfile = usedProviderIds.includes(provider.id);
              return {
                value: provider.id,
                label: provider.name,
                isDisabled: isUsedByOtherProfile && !isCurrentProvider,
              };
            });

            const originalOption = profile.provider_id
              ? options.find((provider) => provider.value === profile.provider_id)
              : null;
            // Use the controlled value (if set) or fall back to the original
            const currentSelectValue =
              selectValues.hasOwnProperty(index) ? selectValues[index] : originalOption;

            return (
              <Flex
                fontSize="sm"
                key={profile.adec_id}
                borderBottom="1px"
                borderColor={theme.colors.dividerColor.light}
                p={2}
                height="60px"
                align="center"
              >
                <HStack spacing={8} flex="1">
                  <Text pl={2} textAlign="left" fontWeight="bold" flexBasis="20%">{profile.name}</Text>
                  <Flex ml={20} justifyContent="flex-start">
                    <ReactSelect
                      key={`select-${index}-${refreshCounter}`}
                      isClearable
                      styles={customStyles}
                      options={options}
                      value={currentSelectValue}
                      onChange={async (selectedOption) => {
                        setSelectValues((prev) => ({ ...prev, [index]: selectedOption }));
                        const updatedProvider = { index, selectedOption };
                        setNewProvider({ index, selectedOption });
                        if (updatedProvider.selectedOption) {
                          return await handleProviderChange(profile, updatedProvider);
                        }
                        await updateProvider(updatedProvider)
                      }}
                      placeholder="None"
                    />
                  </Flex>
                </HStack>
              </Flex>
            );
          })}
        </Box>

      ) : (
        <Center height="100vh" width="100%">
          <VStack spacing={4}>
            <Text>Connect your A-dec+ Clinic to enable Intercom in Link AI. This step must be completed by an A-dec+ Clinic Admin.</Text>
            <Button colorScheme="blue" as="a" href={link}>
              Connect to A-dec+
            </Button>
          </VStack>
        </Center>
      )}
    </>
  );
}

export default AdecSettings;
